import { catchError, forkJoin, Observable, of } from 'rxjs';
import { InstallationReportingMduApiService } from '../../installation-reporting-mdu-api.service';
import { InstallationReportingMduReportContext } from '../../../entities/installation-reporting-mdu-details.entities';
import { SelfInspectionDefinitionV2Response } from './installation-reporting-mdu-bff.entities';
import {
  BUILDING_REPORT_SECTIONS,
  DELIVERY_POINT_REPORT_SECTIONS,
} from './installation-reporting-mdu-section-layout.config';
import {
  InstallationReportSectionConfig,
  InstallationReportSectionId,
} from './installation-reporting-mdu-section.types';
import { mapSelfInspectionDefinition } from './self-inspection-definition.mapper';

type ReportType = InstallationReportingMduReportContext['reportType'];

export type DefinitionsBySection = Partial<
  Record<
    InstallationReportSectionId,
    { items: SelfInspectionDefinitionV2Response[] }
  >
>;

const EMPTY_DEFINITIONS = { items: [] as SelfInspectionDefinitionV2Response[] };

const sectionIdsFor = (reportType: ReportType): InstallationReportSectionId[] =>
  (reportType === 'BUILDING'
    ? BUILDING_REPORT_SECTIONS
    : DELIVERY_POINT_REPORT_SECTIONS
  ).map((section) => section.id);

/** A missing definition set must not prevent the section data from loading. */
const loadDefinitionsOrEmpty = (
  api: InstallationReportingMduApiService,
  sectionId: InstallationReportSectionId,
  reportType: ReportType,
) =>
  api
    .getSelfInspectionDefinitions(sectionId, reportType)
    ?.pipe(catchError(() => of(EMPTY_DEFINITIONS))) ?? of(EMPTY_DEFINITIONS);

export const loadSelfInspectionDefinitions = (
  api: InstallationReportingMduApiService,
  reportType: ReportType,
): Observable<DefinitionsBySection> =>
  forkJoin(
    Object.fromEntries(
      sectionIdsFor(reportType).map((sectionId) => [
        sectionId,
        loadDefinitionsOrEmpty(api, sectionId, reportType),
      ]),
    ),
  ) as Observable<DefinitionsBySection>;

export const withDefinitions = (
  sections: InstallationReportSectionConfig[],
  definitionsBySection: DefinitionsBySection,
  language: string,
): InstallationReportSectionConfig[] =>
  sections.map((section) => ({
    ...section,
    items: [...(definitionsBySection[section.id]?.items ?? [])]
      .sort((a, b) => a.sectionIndex - b.sectionIndex)
      .map((definition) => mapSelfInspectionDefinition(definition, language)),
  }));
